import { registerLocaleData } from "@angular/common";
import {
  provideHttpClient,
  withInterceptors,
  withJsonpSupport,
} from "@angular/common/http";
import localeCa from "@angular/common/locales/ca";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  LOCALE_ID,
  provideExperimentalZonelessChangeDetection,
} from "@angular/core";
import { LuxonDateAdapter } from "@angular/material-luxon-adapter";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from "@angular/router";
import { APIConfigServiceOptions } from "@api/apiconfig.service";
import { ImiauthService } from "@api/controllers/Imiauth";
import { UserService } from "@api/controllers/User";
import { UserCurrentFormService } from "@api/forms/user/current/current.service";
import { appRoutes } from "@app/app.routes";
import { AuthService } from "@app/core/auth/auth.service";
import { provideIcons } from "@app/core/icons/icons.provider";
import { RuntimeConfigService } from "@app/runtime-config.service";
import { provideFuse } from "@fuse";
import { FuseThemeService } from "@fuse/services/theme/theme.service"; // Importa los datos de localización para catalán
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';

import {
  ImiauthHelloFormService,
  ResiduGetTaskStatusFormService,
  ResiduKillTaskFormService,
} from "@api/form-service";
import { LayoutService } from "@utils/layout.service";
import { authInterceptor } from "./core/auth/auth.interceptor";
import { getCatPaginatorIntl } from "./i18n/cat-paginator-intl";
import {
  CategoriaCreateFormService,
  CategoriaDeleteFormService,
  CategoriaListFormService,
  CategoriaPartialUpdateFormService,
  CategoriaReadFormService,
  CategoriaUpdateFormService,
  FraccioCreateFormService,
  FraccioDeleteFormService,
  FraccioListFormService,
  FraccioPartialUpdateFormService,
  FraccioReadFormService,
  FraccioUpdateFormService,
  InformacioSecundariaCreateFormService,
  InformacioSecundariaDeleteFormService,
  InformacioSecundariaListFormService,
  InformacioSecundariaPartialUpdateFormService,
  InformacioSecundariaReadFormService,
  InformacioSecundariaUpdateFormService,
  PuntRecollidaCreateFormService,
  PuntRecollidaDeleteFormService,
  PuntRecollidaListFormService,
  PuntRecollidaPartialUpdateFormService,
  PuntRecollidaReadFormService,
  PuntRecollidaUpdateFormService,
  ResiduCreateFormService,
  ResiduDeleteFormService,
  ResiduListFormService,
  ResiduPartialUpdateFormService,
  ResiduReadFormService,
  ResiduUpdateFormService,
  TipusServeiCreateFormService,
  TipusServeiDeleteFormService,
  TipusServeiListFormService,
  TipusServeiPartialUpdateFormService,
  TipusServeiReadFormService,
  TipusServeiUpdateFormService,
  EspecialCreateFormService,
  EspecialDeleteFormService,
  EspecialListFormService,
  EspecialPartialUpdateFormService,
  EspecialReadFormService,
  EspecialUpdateFormService,
  ResiduExportResidusFormService,
} from "@api/form-service";
import { FraccioService } from "@api/controllers/Fraccio";
import { ResiduService } from "@api/controllers/Residu";
import { PuntRecollidaService } from "@api/controllers/PuntRecollida";
import { CategoriaService } from "@api/controllers/Categoria";
import { InformacioSecundariaService } from "@api/controllers/InformacioSecundaria";
import { HeaderRowOutlet } from "@angular/cdk/table";
import { TipusServeiService } from "@api/controllers/TipusServei";
import { EspecialService } from "@api/controllers/Especial";

registerLocaleData(localeCa, "ca-CA"); // Cambia 'ca-CA' a 'ca-ES' si es necesario
registerLocaleData(localeCa, "ca-ES"); // Registrar el locale catalán como 'ca-ES'

// Initialize runtime configuration values from /assets/runtime-config.json
const runtimeInitializerFn = (runtimeConfig: RuntimeConfigService) => () =>
  runtimeConfig.loadRuntimeConfig();
const IDPInitializerFn = (IDPConfig: RuntimeConfigService) => () =>
  IDPConfig.loadIDPConfig();
const configureControllerService = (runtimeConfig: RuntimeConfigService) => {
  const apiConfigServiceOptions = new APIConfigServiceOptions();
  if (runtimeConfig?.runtimeConfig) {
    apiConfigServiceOptions.apiUrl = runtimeConfig?.config["apiUrl"];
  }
  return apiConfigServiceOptions;
};
export const appConfig: ApplicationConfig = {
  providers: [
    FuseThemeService,
    LayoutService,
    RuntimeConfigService,
    {
      provide: LOCALE_ID,
      useValue: "ca-CA",
    },
    {
      provide: LOCALE_ID,
      useValue: "ca-ES",
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: runtimeInitializerFn,
    //   multi: true,
    //   deps: [RuntimeConfigService],
    // },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: IDPInitializerFn,
    //   multi: true,
    //   deps: [RuntimeConfigService],
    // },
    provideIcons(),
    provideFuse({
      fuse: {
        layout: "classic",
        scheme: "light",
        screens: {
          sm: "600px",
          md: "960px",
          lg: "1280px",
          xl: "1440px",
        },
        theme: "theme-brand",
        themes: [
          {
            id: "theme-default",
            name: "Default",
          },
          {
            id: "theme-brand",
            name: "Brand",
          },
          {
            id: "theme-teal",
            name: "Teal",
          },
          {
            id: "theme-rose",
            name: "Rose",
          },
          {
            id: "theme-purple",
            name: "Purple",
          },
          {
            id: "theme-amber",
            name: "Amber",
          },
        ],
      },
    }),
    provideAnimationsAsync(),
    provideHttpClient(withJsonpSupport()),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideExperimentalZonelessChangeDetection(),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" })
    ),
    ImiauthService,
    ImiauthHelloFormService,
    AuthService,
    UserCurrentFormService,
    UserService,
    FraccioCreateFormService,
    FraccioReadFormService,
    FraccioListFormService,
    FraccioDeleteFormService,
    FraccioPartialUpdateFormService,
    FraccioUpdateFormService,
    FraccioService,
    ResiduCreateFormService,
    ResiduReadFormService,
    ResiduListFormService,
    ResiduDeleteFormService,
    ResiduPartialUpdateFormService,
    ResiduUpdateFormService,
    ResiduExportResidusFormService,
    ResiduService,
    PuntRecollidaCreateFormService,
    PuntRecollidaReadFormService,
    PuntRecollidaListFormService,
    PuntRecollidaDeleteFormService,
    PuntRecollidaPartialUpdateFormService,
    PuntRecollidaUpdateFormService,
    PuntRecollidaService,
    CategoriaCreateFormService,
    CategoriaReadFormService,
    CategoriaListFormService,
    CategoriaDeleteFormService,
    CategoriaPartialUpdateFormService,
    CategoriaUpdateFormService,
    CategoriaService,
    InformacioSecundariaCreateFormService,
    InformacioSecundariaReadFormService,
    InformacioSecundariaListFormService,
    InformacioSecundariaDeleteFormService,
    InformacioSecundariaPartialUpdateFormService,
    InformacioSecundariaUpdateFormService,
    InformacioSecundariaService,
    TipusServeiCreateFormService,
    TipusServeiReadFormService,
    TipusServeiListFormService,
    TipusServeiDeleteFormService,
    TipusServeiPartialUpdateFormService,
    TipusServeiUpdateFormService,
    TipusServeiService,
    EspecialCreateFormService,
    EspecialReadFormService,
    EspecialListFormService,
    EspecialDeleteFormService,
    EspecialPartialUpdateFormService,
    EspecialUpdateFormService,
    EspecialService,
    ResiduGetTaskStatusFormService,
    ResiduKillTaskFormService,
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    { provide: MatPaginatorIntl, useValue: getCatPaginatorIntl() },

    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "D",
        },
        display: {
          dateInput: "DDD",
          monthYearLabel: "LLL yyyy",
          dateA11yLabel: "DD",
          monthYearA11yLabel: "LLLL yyyy",
        },
      },
    },

    // Transloco Config
    {
      provide: APIConfigServiceOptions,
      useFactory: configureControllerService,
      deps: [RuntimeConfigService],
    },
    // provideTransloco({
    //   config: {
    //     availableLangs: [
    //       {
    //         id: "en",
    //         label: "English",
    //       },
    //       {
    //         id: "ca",
    //         label: "Catalá",
    //       },
    //     ],
    //     defaultLang: "en",
    //     fallbackLang: "en",
    //     reRenderOnLangChange: true,
    //     prodMode: true,
    //   },
    //   loader: TranslocoHttpLoader,
    // }),
    // {
    //   // Preload the default language before the app starts to prevent empty/jumping content
    //   provide: APP_INITIALIZER,
    //   useFactory: () => {
    //     const translocoService = inject(TranslocoService);
    //     const defaultLang = translocoService.getDefaultLang();
    //     translocoService.setActiveLang(defaultLang);

    //     return () => firstValueFrom(translocoService.load(defaultLang));
    //   },
    //   multi: true,
    // },
    // { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  ],
};
